import { ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Row, SortingState } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import MobilePageHeader from 'components/shared/MobilePageHeader';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import ControlledTable from 'components/shared/table/ControlledTable';
import usePagination from 'hooks/usePagination';
import useWorkflows from 'hooks/useWorkflows';
import Workflow from 'models/Workflow';
import { Body, H2 } from 'styles/typography';

import inboundReferralsTableColumns from './inboundReferralsTableColumns';
import { orderWorkflows } from './referralProgress';

interface Props {
  title: string;
  // The workflow type(s) this inbox shows (definition apiName). A list because Portal
  // Automation shows both of its types — retrieval runs and discovery sweeps — in one table.
  apiNames: string[];
  // Row-click destination base; rows navigate to `${basePath}/${id}`.
  basePath: string;
  // Optional header control (e.g. the New referral button).
  headerAction?: ReactNode;
}

// The one inbox implementation, shared by the per-type list pages. The index is server-filtered
// to apiNames and server-paginated; each page just names which workflow type(s) it wants.
export default function WorkflowListPage({ title, apiNames, basePath, headerAction }: Props) {
  const navigate = useNavigate();
  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>([]);

  const { workflows, meta, isLoading, isError } = useWorkflows({
    apiName: apiNames,
    page: pagination.paging.page,
    pageSize: pagination.paging.pageSize,
    // No sortable columns on this table (per-column sorting is disabled below); createdAt
    // desc is the API's own default order too, made explicit here for pagination stability —
    // an unsorted paginated list can repeat/skip rows across pages.
    sortBy: 'createdAt desc',
  });

  // An open sweep still pins to the top of whichever page it lands on (OLIO-2814); everything
  // else keeps the server's createdAt-desc order. This runs over one page of rows, not the
  // full inbox, which is why the pin is "top of this page" rather than "top of the list" once
  // there's more than a page of runs — see orderWorkflows for the rule itself.
  const rows = useMemo(() => orderWorkflows(workflows), [workflows]);

  const columns = useMemo(() => inboundReferralsTableColumns(), []);

  return (
    <Page>
      <DesktopOnly>
        <Page.Header>
          <HeaderInner>
            <H2>{title}</H2>
            {headerAction}
          </HeaderInner>
        </Page.Header>
      </DesktopOnly>
      <MobileOnly>
        <MobilePageHeader title={title} actionElement={headerAction} />
      </MobileOnly>
      <Page.Content>
        {isError ? (
          <Body>We couldn’t load referrals.</Body>
        ) : (
          <ControlledTable<Workflow>
            data={rows}
            columns={columns}
            loading={isLoading}
            pageCount={meta?.totalPages ?? 1}
            pagination={pagination}
            sorting={{ sortBy: sort, setSortBy: setSort }}
            onRowClick={(row: Row<Workflow>) => navigate(`${basePath}/${row.original.id}`)}
          />
        )}
      </Page.Content>
    </Page>
  );
}

const HeaderInner = styled.div`
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;
